<template>
  <el-container class="background-white">
    <el-header class="header-container">
      <h1 class="header-title">Pending FBA Orders <small>(Amazon FBA orders awaiting payment &gt; 60 days)</small></h1>
    </el-header>

    <el-main class="main-content">
      <el-row :gutter="20" class="controls-row" align="middle">
        <el-col :xs="24" :sm="12">
          <el-switch
              v-model="autoRefresh"
              :disabled="loading"
              active-text="Auto-Refresh On"
              inactive-text="Auto-Refresh Off"
              class="custom-switch">
          </el-switch>
        </el-col>

        <el-col :xs="24" :sm="12" class="text-right">
          <el-button
              type="primary"
              size="mini"
              @click="fetch"
              :disabled="loading || autoRefresh">
            <i class="el-icon-refresh"></i>
            Refresh
          </el-button>
        </el-col>
      </el-row>

      <el-row>
        <el-col :span="24">
          <div class="table-container">
            <el-table
                v-loading="loading"
                :data="orders"
                class="order-table"
                stripe
                border
                max-height="600">
              <el-table-column
                  prop="sales_channel"
                  label="Sales Channel"
                  min-width="130">
                <template #default="{ row }">
                  <span class="table-cell">{{ row.sales_channel }}</span>
                </template>
              </el-table-column>
              <el-table-column
                  prop="region"
                  label="Store"
                  min-width="100">
                <template #default="{ row }">
                  <span class="table-cell">{{ row.region }}</span>
                </template>
              </el-table-column>
              <el-table-column
                  prop="order_reference"
                  label="Order ID"
                  min-width="180">
                <template #default="{ row }">
                  <a
                      v-if="row.order_link"
                      :href="row.order_link"
                      class="order-id"
                      target="_blank"
                      rel="noopener noreferrer">
                    {{ row.order_reference }}
                  </a>
                  <span v-else class="order-id">{{ row.order_reference }}</span>
                </template>
              </el-table-column>
              <el-table-column
                  prop="fulfillment_channel"
                  label="Fulfillment Channel"
                  min-width="160">
                <template #default="{ row }">
                  <span class="table-cell">{{ row.fulfillment_channel }}</span>
                </template>
              </el-table-column>
              <el-table-column
                  label="Created At"
                  min-width="180">
                <template #default="{ row }">
                  <span class="date-cell">{{ displayDate(row.created_at) }}</span>
                </template>
              </el-table-column>
              <el-table-column
                  prop="amazon_status"
                  label="Amazon Status"
                  min-width="150">
                <template #default="{ row }">
                  <el-tag
                      v-if="row.amazon_status"
                      :type="statusTagType(row.amazon_status)"
                      size="small">
                    {{ row.amazon_status }}
                  </el-tag>
                  <span v-else class="table-cell text-muted">Not checked</span>
                </template>
              </el-table-column>
              <el-table-column
                  label="Last Checked"
                  min-width="180">
                <template #default="{ row }">
                  <span class="date-cell">{{ displayDate(row.amazon_status_checked_at) }}</span>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </el-col>
      </el-row>
    </el-main>
  </el-container>
</template>

<style scoped>
.header-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 15px 25px;
  border-bottom: 2px solid #e8e8e8;
  background: #f8f9fa;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.header-title {
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
}

.main-content {
  padding: 25px;
  background: #fff;
}

.controls-row {
  margin-bottom: 25px;
  align-items: center;
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
}

.text-right {
  text-align: right;
}

.table-container {
  width: 100%;
  overflow-x: auto;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.order-table {
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
}

.table-cell {
  color: #2c3e50;
  font-weight: 500;
}

.text-muted {
  color: #909399;
  font-style: italic;
}

.order-id {
  color: #409EFF;
  font-weight: 600;
}

.date-cell {
  color: #606266;
  font-size: 0.9em;
}

@media screen and (max-width: 768px) {
  .controls-row .el-col {
    margin-bottom: 15px;
    text-align: left !important;
  }

  .header-title {
    font-size: 20px;
  }

  .main-content {
    padding: 15px;
  }
}
</style>

<script>
import axios from '../../../repositories';

export default {
  name: 'PendingFbaOrdersPage',
  created() {
    this.fetch();
  },
  data() {
    return {
      autoRefresh: false,
      autoRefreshInterval: 600000,
      refreshInterval: null,
      loading: true,
      orders: [],
    };
  },
  methods: {
    async fetch() {
      this.loading = true;
      try {
        const response = await axios.get('/kitsilano/orders/pending-fba-orders');
        if (response.data?.success === true || response.data?.success === 'true') {
          this.orders = response.data?.pending_fba_orders || [];
        } else {
          this.$message.error(response.data?.message || 'Error loading pending FBA orders');
        }
      } catch (err) {
        this.$message.error(err.message || 'Error occurred loading pending FBA orders');
      } finally {
        this.loading = false;
      }
    },
    displayDate(date) {
      if (!date) {
        return '-';
      }
      try {
        const formattedDate = new Date(date);
        return formattedDate.toLocaleString('en-CA', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZoneName: 'short',
        });
      } catch (error) {
        return '-';
      }
    },
    statusTagType(status) {
      if (status === 'Pending') return 'warning';
      if (status === 'Canceled' || status === 'Unfulfillable') return 'danger';
      if (status === 'Shipped' || status === 'PartiallyShipped') return 'success';
      return 'info';
    },
  },
  watch: {
    autoRefresh: function (val) {
      if (val) {
        return this.refreshInterval = setInterval(
            () => this.fetch(),
            this.autoRefreshInterval,
        );
      }

      clearTimeout(this.refreshInterval);
    },
  },
};
</script>
